import { useState, useEffect } from 'react';
import { Shield, Swords } from 'lucide-react';
import NicknameBar from '../components/NicknameBar';
import OrnamentSeparator from '../components/OrnamentSeparator';

interface SheetVoteWinnerScreenProps {
  sheetId: string;
  sheetName: string;
  showCelebration?: boolean; // true = appena votato, false = standby normale
}

interface SheetDetails {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  alignment?: string;
  notes?: string;
  damage?: number;
  armor?: number;
  maxHP?: number;
  level?: number;
}

interface BondInfo {
  id: string;
  text: string | null;
}

/**
 * Sheet Vote Winner Screen (Audience)
 *
 * Mostra l'eroe vincitore per la squadra dell'utente.
 */
function SheetVoteWinnerScreen({ sheetId, sheetName, showCelebration = true }: SheetVoteWinnerScreenProps) {
  const [sheet, setSheet] = useState<SheetDetails | null>(null);
  const [bonds, setBonds] = useState<BondInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSheetDetails();
    loadBonds();
  }, [sheetId]);

  const loadSheetDetails = async () => {
    try {
      const response = await fetch(`/api/sheets/${sheetId}`);
      if (response.ok) {
        const data = await response.json();
        setSheet(data.sheet);
      }
    } catch (err) {
      console.error('Failed to load sheet details:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBonds = async () => {
    try {
      const teamColor = localStorage.getItem('teamColor');
      const sessionId = localStorage.getItem('sessionId');

      if (!teamColor || !sessionId) return;

      const response = await fetch(`/api/bond-votes/session/${sessionId}/team/${teamColor}`);
      if (response.ok) {
        const data = await response.json();
        setBonds(data.bonds || []);
      }
    } catch (err) {
      console.error('Failed to load bonds:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400 mx-auto mb-4"></div>
          <p className="text-ink-light text-xl font-heading">Caricamento eroe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto p-4 pt-10 pb-8">
      <NicknameBar />
      <div className="max-w-md w-full mx-auto text-center">
        {/* Celebration Header - solo se showCelebration è true */}
        {showCelebration && (
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold text-ink mb-1">
              Il Vostro Eroe
            </h1>
            <p className="text-ink-light">
              La squadra ha scelto!
            </p>
            <OrnamentSeparator />
          </div>
        )}

        {/* Character Card */}
        <div className="parchment-card rounded-2xl p-6 border-2 border-parchment-400 shadow-2xl">
          {/* Character Image - aspect ratio 366:466 ≈ 0.785 */}
          {sheet?.imageUrl ? (
            <div className="w-full aspect-[366/466] mb-4">
              <img
                src={sheet.imageUrl}
                alt={sheet.name}
                className="w-full h-full object-contain rounded-lg shadow-lg"
              />
            </div>
          ) : (
            <div className="w-full aspect-[366/466] parchment-panel rounded-lg mb-4 flex items-center justify-center">
              <span className="text-8xl">&#9876;</span>
            </div>
          )}

          {/* Character Name */}
          <h2 className="text-3xl font-heading font-bold text-ink font-display mb-3">
            {sheet?.name || sheetName}
          </h2>

          {/* Character Description */}
          {sheet?.description && (
            <p className="text-ink-light text-base text-left whitespace-pre-line">
              {sheet.description}
            </p>
          )}

          {/* Combat Stats */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <div className="parchment-panel rounded-lg p-2 text-center">
              <p className="text-[10px] text-ink-light uppercase">Danno</p>
              <p className="text-xl font-display font-bold text-ink">{sheet?.damage ?? 0}</p>
            </div>
            <div className="parchment-panel rounded-lg p-2 text-center">
              <p className="text-[10px] text-ink-light uppercase">Armatura</p>
              <p className="text-xl font-display font-bold text-ink">{sheet?.armor ?? 0}</p>
            </div>
            <div className="parchment-panel rounded-lg p-2 text-center">
              <p className="text-[10px] text-ink-light uppercase">PF Max</p>
              <p className="text-xl font-display font-bold text-ink">{sheet?.maxHP ?? 10}</p>
            </div>
            <div className="parchment-panel rounded-lg p-2 text-center">
              <p className="text-[10px] text-ink-light uppercase">Livello</p>
              <p className="text-xl font-display font-bold text-ink">{sheet?.level ?? 1}</p>
            </div>
          </div>

          {/* Stats Grid - Bonuses & Maluses */}
          {((sheet?.bonuses && sheet.bonuses.length > 0) || (sheet?.maluses && sheet.maluses.length > 0)) && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              {/* Bonuses */}
              {sheet?.bonuses && sheet.bonuses.length > 0 && (
                <div className="parchment-panel rounded-lg p-3 border border-team-green/30">
                  <h3 className="text-base font-semibold text-team-green mb-2 flex items-center gap-1">
                    <Shield size={14} />
                    Bonus
                  </h3>
                  <ul className="space-y-1">
                    {sheet.bonuses.map((bonus, idx) => (
                      <li key={idx} className="text-ink-light text-sm flex justify-between">
                        <span>{bonus.attribute}</span>
                        <span className="text-team-green font-bold">+{bonus.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Maluses */}
              {sheet?.maluses && sheet.maluses.length > 0 && (
                <div className="parchment-panel rounded-lg p-3 border border-team-red/30">
                  <h3 className="text-base font-semibold text-team-red mb-2 flex items-center gap-1">
                    <Swords size={14} />
                    Malus
                  </h3>
                  <ul className="space-y-1">
                    {sheet.maluses.map((malus, idx) => (
                      <li key={idx} className="text-ink-light text-sm flex justify-between">
                        <span>{malus.attribute}</span>
                        <span className="text-team-red font-bold">{malus.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Alignment */}
          {sheet?.alignment && (
            <div className="mt-4 parchment-panel rounded-lg p-3 border border-parchment-400">
              <h3 className="text-base font-semibold text-team-yellow mb-1">Allineamento</h3>
              <p className="text-ink text-base">{sheet.alignment}</p>
            </div>
          )}

          {/* Moves */}
          {sheet?.moves && sheet.moves.length > 0 && (
            <div className="mt-4 parchment-panel rounded-lg p-3 border border-parchment-300">
              <h3 className="text-base font-semibold text-ink mb-2">Mosse</h3>
              <div className="space-y-2">
                {sheet.moves.map((move, idx) => (
                  <div key={idx} className="parchment-panel rounded p-2 text-left">
                    <h4 className="font-semibold text-ink text-base">{move.name}</h4>
                    <p className="text-ink-light text-sm">{move.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Character Notes */}
          {sheet?.notes && (
            <div className="mt-4 pt-4 border-t border-parchment-400">
              <p className="text-ink-light italic text-base text-left whitespace-pre-line">
                {sheet.notes}
              </p>
            </div>
          )}

          {/* Bonds */}
          {bonds.length > 0 && (
            <div className="mt-4 pt-4 border-t border-parchment-400">
              <p className="text-ink-light text-sm uppercase mb-2">Legami</p>
              <div className="space-y-2">
                {bonds.filter(b => b.text).map((bond) => (
                  <p key={bond.id} className="text-ink-light text-base italic">
                    "{bond.text}"
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6">
          <p className="text-ink-light text-base">
            Preparati all'avventura!
          </p>
        </div>
      </div>
    </div>
  );
}

export default SheetVoteWinnerScreen;
